import { useEffect, useState } from 'react';

import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import styled from '@mui/material/styles/styled';
import { useTranslation } from 'react-i18next';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';

import { crawlerApis } from '../../../apis/crawlerApis';
import Loading from '../../../components/elements/loading/Loading';
import CrawlerStatusTable from '../../../components/page-sections/web-crawler/crawler-status/CrawlerStatusTable';
import Breadcrumbs from '../../../components/sections/breadcrumbs/Breadcrumbs';
import TitlePanel from '../../../components/sections/title-panel/TitlePanel';
import useToast, { TOAST_TYPE } from '../../../hooks/useToast';
import { buildPath, PRIVATE_ROUTE } from '../../../routes/routes';
import { sentenceCase } from '../../../utils/utils';

const StyledTitleWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  marginBottom: '1em',
  justifyContent: 'space-between',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}));

const StyledButtonWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    width: '100%',
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginRight: '1em',
  [theme.breakpoints.down('md')]: {
    marginRight: 0,
    marginBottom: '1em',
  },
}));

const ProjectCrawlerStatusDetailsPage = () => {
  const { projectId, crawlerJobId } = useParams();
  const { showToast } = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [status, setStatus] = useState(null);

  const handleGetJobStatus = async (jobSlotId) => {
    try {
      const { data: statusRes } = await crawlerApis.getCrawlJobStatus(
        projectId,
        jobSlotId
      );

      setStatus(statusRes);
    } catch (error) {
      showToast(
        TOAST_TYPE.ERROR,
        sentenceCase(
          t('fetch', {
            ns: 'errors',
            content: t('crawler.jobStatus', { ns: 'sectionTitles' }),
          })
        )
      );
      setStatus(null);
    }
  };

  const fetchCrawlJob = async () => {
    setLoading(true);
    try {
      const { data: crawlJobRes } = await crawlerApis.getCrawlJob(
        projectId,
        crawlerJobId
      );
      setJob(crawlJobRes);
      await handleGetJobStatus(crawlerJobId);
    } catch (error) {
      return navigate(PRIVATE_ROUTE.ERROR404, { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId || !crawlerJobId) return;
    fetchCrawlJob();
  }, []);

  return (
    <Container>
      {loading ? (
        <Loading />
      ) : (
        <>
          <Breadcrumbs jobName={job?.name} />
          <StyledTitleWrapper>
            <TitlePanel
              visible
              title={t('crawler.jobStatus', { ns: 'sectionTitles' })}
            />
            <StyledButtonWrapper>
              <StyledButton
                variant="text"
                component={RouterLink}
                to={buildPath({
                  path: PRIVATE_ROUTE.PROJECT_CRAWLER,
                  params: { projectId },
                })}
              >
                {t('returnToJobOverview', { ns: 'buttons' })}
              </StyledButton>
              <Button onClick={fetchCrawlJob}>
                {t('reload', { ns: 'buttons' })}
              </Button>
            </StyledButtonWrapper>
          </StyledTitleWrapper>

          <CrawlerStatusTable job={job} status={status} />
        </>
      )}
    </Container>
  );
};

export default ProjectCrawlerStatusDetailsPage;
